import React, { useState } from 'react';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Icon from '../../../../components/AppIcon';
import { aiService } from '../../../../services/aiService';
import Button from '../../../../components/ui/Button';


const MarketplacesTab = ({ productData, onFieldChange }) => {
  const [recommendingMarketplaces, setRecommendingMarketplaces] = useState(false);
  const [recommendations, setRecommendations] = useState(null);

  const listingTypeOptions = [
    { value: 'gold_special', label: 'Gold Special' },
    { value: 'gold_pro', label: 'Gold Pro' },
    { value: 'gratuito', label: 'Gratuito' }
  ];

  const shippingTypeOptions = [
    { value: 'seller', label: 'Vendedor' },
    { value: 'shopee_envios', label: 'Shopee Envios' }
  ];

  const handleRecommendMarketplaces = async () => {
    try {
      setRecommendingMarketplaces(true);
      
      const prompt = `Recomende os melhores marketplaces para este produto:
Nome: ${productData?.name}
Categoria: ${productData?.category}
Preço: R$ ${productData?.price || 0}
Dimensões: ${productData?.width}x${productData?.height}x${productData?.length}cm
Peso: ${productData?.weight}kg

Retorne um JSON com array de marketplaces ordenados por prioridade:
[
  { "marketplace": "nome", "score": 1-10, "reasoning": "justificativa" }
]

Marketplaces disponíveis: Mercado Livre, Shopee, Amazon, Magazine Luiza`;

      const response = await aiService?.generateContent(prompt, { maxTokens: 400 });
      
      if (response?.success) {
        try {
          const recs = JSON.parse(response?.content);
          setRecommendations(recs);
        } catch (e) {
          // Fallback recommendations
          setRecommendations([
            { marketplace: 'Mercado Livre', score: 9, reasoning: 'Maior alcance no Brasil' },
            { marketplace: 'Shopee', score: 7, reasoning: 'Bom para produtos de preço médio' },
            { marketplace: 'Amazon', score: 6, reasoning: 'Público premium' },
            { marketplace: 'Magazine Luiza', score: 5, reasoning: 'Forte em eletrônicos' }
          ]);
        }
      } else {
        throw new Error('IA não disponível');
      }
    } catch (error) {
      console.error('Error recommending marketplaces:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Configure a IA em AI Assistant Hub primeiro',
          progress: 100
        }
      }));
    } finally {
      setRecommendingMarketplaces(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* AI Marketplace Recommendations */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={18} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Quais Marketplaces Vender?</span>
          </div>
          <Button
            onClick={handleRecommendMarketplaces}
            disabled={recommendingMarketplaces}
            variant="primary"
            size="sm"
          >
            {recommendingMarketplaces ? (
              <Icon name="Loader" size={14} className="animate-spin" />
            ) : (
              <>
                <Icon name="Target" size={14} className="mr-1" />
                Analisar
              </>
            )}
          </Button>
        </div>

        {recommendations && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-2">Ranking de Marketplaces:</p>
            {recommendations?.map((rec, index) => (
              <div key={index} className="bg-background rounded-md p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-primary">{index + 1}.</span>
                    <span className="font-semibold text-foreground">{rec?.marketplace}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 10 })?.map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-3 rounded-sm ${
                          i < rec?.score ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{rec?.reasoning}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Mercado Livre */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
              <Icon name="Store" size={20} color="#000" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Mercado Livre</h3>
              <p className="text-xs text-muted-foreground">Maior marketplace da América Latina</p>
            </div>
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.mercadolivreActive}
              onChange={(e) => onFieldChange('mercadolivreActive', e?.target?.checked)}
            />
            <span className="text-sm font-medium text-foreground">Ativo</span>
          </label>
        </div>

        {productData?.mercadolivreActive && (
          <div className="space-y-4">
            <Input
              label="ID Categoria ML"
              value={productData?.mercadolivreCategory}
              onChange={(e) => onFieldChange('mercadolivreCategory', e?.target?.value)}
              placeholder="MLB12345"
            />
            
            <Select
              label="Tipo de Anúncio"
              options={listingTypeOptions}
              value={productData?.mercadolivreListingType}
              onChange={(value) => onFieldChange('mercadolivreListingType', value)}
            />
            
            <label className="flex items-center gap-2 cursor-pointer">
              <Input
                type="checkbox"
                checked={productData?.mercadolivreFreeShipping}
                onChange={(e) => onFieldChange('mercadolivreFreeShipping', e?.target?.checked)}
              />
              <span className="text-sm text-foreground">Frete Grátis</span>
            </label>
          </div>
        )}
      </div>
      {/* Shopee */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Icon name="ShoppingBag" size={20} color="#fff" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Shopee</h3>
              <p className="text-xs text-muted-foreground">Marketplace em crescimento</p>
            </div>
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.shopeeActive}
              onChange={(e) => onFieldChange('shopeeActive', e?.target?.checked)}
            />
            <span className="text-sm font-medium text-foreground">Ativo</span>
          </label>
        </div>

        {productData?.shopeeActive && (
          <div className="space-y-4">
            <Input
              label="ID Categoria Shopee"
              value={productData?.shopeeCategory}
              onChange={(e) => onFieldChange('shopeeCategory', e?.target?.value)}
              placeholder="100001"
            />
            
            <Select
              label="Condição de Frete"
              options={shippingTypeOptions}
              value={productData?.shopeeShippingType}
              onChange={(value) => onFieldChange('shopeeShippingType', value)}
            />
          </div>
        )}
      </div>
      {/* Amazon */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <Icon name="Package" size={20} color="#fff" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Amazon</h3>
              <p className="text-xs text-muted-foreground">Marketplace global</p>
            </div>
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.amazonActive}
              onChange={(e) => onFieldChange('amazonActive', e?.target?.checked)}
            />
            <span className="text-sm font-medium text-foreground">Ativo</span>
          </label>
        </div>
      </div>
      {/* Magazine Luiza */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Icon name="Store" size={20} color="#fff" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Magazine Luiza</h3>
              <p className="text-xs text-muted-foreground">Marketplace nacional</p>
            </div>
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.magaluActive}
              onChange={(e) => onFieldChange('magaluActive', e?.target?.checked)}
            />
            <span className="text-sm font-medium text-foreground">Ativo</span>
          </label>
        </div>
      </div>
      {/* Sync Options */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Sincronização Automática</h3>
        
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.syncStock}
              onChange={(e) => onFieldChange('syncStock', e?.target?.checked)}
            />
            <span className="text-sm text-foreground">Sincronizar estoque automaticamente</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <Input
              type="checkbox"
              checked={productData?.syncPrice}
              onChange={(e) => onFieldChange('syncPrice', e?.target?.checked)}
            />
            <span className="text-sm text-foreground">Sincronizar preço automaticamente</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default MarketplacesTab;